import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import {
  Instagram as InstagramIcon,
  Google as GoogleIcon,
  AssignmentTurnedIn as ResolvedIcon,
  HistoryToggleOff as NotResolvedIcon,
  NewReleases as NewIcon,
  Pending as InProgressIcon,
  Timeline as TimelineIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { UnifiedFeedback } from '../../types/unifiedFeedback';
import { DMIcon, GCCIcon, TikTokIcon } from './PlatformIcons';

interface StatusHistoryItem {
  id: number;
  feedback_id: string;
  platform: string;
  status: string;
  note: string;
  changed_at: string;
  user_email: string;
  user_name?: string;
  outlet_pic_id?: string;
  outlet_pic_name?: string;
}

export interface ComplaintHistoryDialogProps {
  open: boolean;
  feedback: UnifiedFeedback | null;
  onClose: () => void;
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'new':
      return <NewIcon color="info" />;
    case 'in_progress':
      return <InProgressIcon color="warning" />;
    case 'resolved':
      return <ResolvedIcon color="success" />;
    case 'not_resolved':
      return <NotResolvedIcon color="error" />;
    default:
      return <TimelineIcon />;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'new':
      return 'info';
    case 'in_progress':
      return 'warning';
    case 'resolved':
      return 'success';
    case 'not_resolved':
      return 'error';
    default:
      return 'info';
  }
};

const getPlatformIcon = (platform: string) => {
  switch (platform) {
    case 'instagram':
      return <InstagramIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />;
    case 'google':
      return <GoogleIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />;
    case 'tiktok':
      return <TikTokIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />;
    case 'gcc':
      return <GCCIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />;
    case 'dm':
      return <DMIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />;
    default:
      return null;
  }
};

const getPlatformLabel = (platform?: string) => {
  switch (platform) {
    case 'instagram':
      return 'Instagram';
    case 'google':
      return 'Google';
    case 'tiktok':
      return 'TikTok';
    case 'gcc':
      return 'GCC';
    case 'dm':
      return 'Direct Message';
    default:
      return 'Feedback';
  }
};

export const ComplaintHistoryDialog: React.FC<ComplaintHistoryDialogProps> = ({
  open,
  feedback,
  onClose,
}) => {
  const [history, setHistory] = useState<StatusHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && feedback) {
      void fetchHistory(feedback.id);
    }
  }, [open, feedback]);

  const fetchHistory = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/feedback/complaint-history/${id}`);
      if (response.data.success) {
        setHistory(response.data.data);
      } else {
        setError('Failed to fetch history');
      }
    } catch (fetchError) {
      console.error('Error fetching history:', fetchError);
      setError('Error fetching history');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Status History - {getPlatformLabel(feedback?.platform)} Feedback</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : history.length === 0 ? (
          <Alert severity="info">No history found</Alert>
        ) : (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Showing status change history for{' '}
              <Box component="span" fontWeight="bold">
                {feedback?.author}
              </Box>
            </Typography>

            {history.map((item) => (
              <Card key={item.id} sx={{ mb: 1 }}>
                <CardContent>
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                    <Box>
                      <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                        {getPlatformIcon(item.platform)}
                        <Chip
                          size="small"
                          icon={getStatusIcon(item.status)}
                          label={item.status.replace('_', ' ').toUpperCase()}
                          color={
                            getStatusColor(item.status) as
                              | 'default'
                              | 'primary'
                              | 'secondary'
                              | 'error'
                              | 'info'
                              | 'success'
                              | 'warning'
                          }
                        />
                      </Box>
                      {item.status === 'in_progress' && (
                        <Chip
                          size="small"
                          icon={<PersonIcon fontSize="small" />}
                          label={
                            item.outlet_pic_name
                              ? `Assigned to: ${item.outlet_pic_name}`
                              : 'Not assigned to any PIC'
                          }
                          variant="outlined"
                          color={item.outlet_pic_name ? 'primary' : 'default'}
                          sx={{ mt: 1, mb: 1 }}
                        />
                      )}
                      <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                        Note: {item.note || 'No notes provided'}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="caption" color="text.secondary" display="block">
                        Changed by: {item.user_email || item.user_name || 'System'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {new Date(item.changed_at).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
